/**
 * AdminVerify - Re-verify an admin session (2FA) without logging out.
 *
 * Admin sessions expire after 12 hours. Previously the only way to get a new
 * one was to sign out and sign in again. This page lets a logged-in admin
 * request a fresh one-time code and unlock the admin area directly.
 */

import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAdminRole } from "@/hooks/useAdminRole";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { ShieldCheck } from "lucide-react";

const AdminVerify = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const { isAdmin, isVerifiedAdmin, loading, refresh } = useAdminRole();

  const [code, setCode] = useState("");
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [sent, setSent] = useState(false);

  const redirectTo = (location.state as { from?: string } | null)?.from || "/admin";

  useEffect(() => {
    if (loading) return;
    if (!isAdmin) {
      navigate("/", { replace: true });
      return;
    }
    if (isVerifiedAdmin) {
      navigate(redirectTo, { replace: true });
    }
  }, [loading, isAdmin, isVerifiedAdmin, navigate, redirectTo]);

  const sendCode = async () => {
    setSending(true);
    const { error } = await supabase.functions.invoke("send-admin-otp", { body: {} });
    setSending(false);

    if (error) {
      toast({
        title: "Kunde inte skicka verifieringskod",
        description: "Försök igen om en stund.",
        variant: "destructive",
      });
      return;
    }

    setSent(true);
    toast({
      title: "Verifieringskod skickad",
      description: "En verifieringskod har skickats till din e-post.",
    });
  };

  const verify = async () => {
    if (code.length !== 6) return;
    setVerifying(true);

    const { data, error } = await supabase.functions.invoke("verify-admin-otp", {
      body: { code },
    });

    if (error || !data?.valid) {
      setVerifying(false);
      setCode("");
      toast({
        title: "Fel kod",
        description: data?.error || "Koden är ogiltig eller har gått ut.",
        variant: "destructive",
      });
      return;
    }

    await refresh();
    setVerifying(false);
    toast({ title: "Verifierad", description: "Din adminsession är aktiv i 12 timmar." });
    navigate(redirectTo, { replace: true });
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mx-auto mb-2 flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
            <ShieldCheck className="h-6 w-6 text-primary" aria-hidden="true" />
          </div>
          <CardTitle>Verifiera adminsession</CardTitle>
          <CardDescription>
            Av säkerhetsskäl gäller en adminsession i 12 timmar. Begär en engångskod till din
            e-post för att låsa upp adminpanelen igen.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {!sent ? (
            <Button className="w-full" onClick={sendCode} disabled={sending}>
              {sending ? "Skickar kod..." : "Skicka verifieringskod"}
            </Button>
          ) : (
            <>
              <div className="flex justify-center">
                <InputOTP maxLength={6} value={code} onChange={setCode}>
                  <InputOTPGroup>
                    {[0, 1, 2, 3, 4, 5].map((i) => (
                      <InputOTPSlot key={i} index={i} />
                    ))}
                  </InputOTPGroup>
                </InputOTP>
              </div>
              <Button className="w-full" onClick={verify} disabled={verifying || code.length !== 6}>
                {verifying ? "Verifierar..." : "Verifiera"}
              </Button>
              <Button variant="ghost" className="w-full" onClick={sendCode} disabled={sending}>
                Skicka ny kod
              </Button>
            </>
          )}
          <Button variant="outline" className="w-full" onClick={() => navigate("/")}>
            Tillbaka till startsidan
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminVerify;
